import dayjs from 'dayjs';
import UTC from 'dayjs/plugin/utc';
import { DateRangeTypeOption } from '../data/providers/settings/dates/dateRangeProvider';
import { IRange } from '../data/types';
dayjs.extend(UTC);

/**
 * @description formats date and time (ex. 1/11/21 1:30:05 PM)
 * @param timestamp
 * @returns
 */
export const formatDateWithTime = (timestamp: number): string => {
  return dayjs(timestamp).format('M/DD/YY h:mm:ssa');
};
/**
 * @descirption formats today (ex. 02/20/21)
 * @returns
 */
export const formatToday = (): string =>
  dayjs().endOf('day').format('MM/DD/YY');

/**
 * @description creates date range from dateType and dateRange
 * @param dateType
 * @param dateRange
 * @returns
 */
export const toDateRangeString = (
  dateType: DateRangeTypeOption,
  dateRange: IRange
): string => {
  if (dateType.value === 'custom') {
    return `${dateRange.from.label}-${dateRange.to.label}`;
  }
  return dateType.label;
};

/**
 * @description formats day from utc timestamp (ex. 10/20/1981)
 * @param timestamp
 * @returns
 */
export const formatDayFromUtc = (timestamp: number): string =>
  dayjs.utc(timestamp).format('MM/DD/YY');

/**
 * @description formats day (ex. 10/20/1981)
 * @param timestamp
 * @returns
 */
export const formatDay = (timestamp: number): string =>
  dayjs(timestamp).format('MM/DD/YY');

/**
 * @description timestamp of the start of today
 */
export const startOfToday = (): number =>
  dayjs().utc().startOf('day').valueOf();

/**
 * @description formats unix time to local time (ex. January 14th, 20)
 * @param timestamp
 * @returns
 */
export const formatFromUnix = (timestamp: number): string => {
  const offset = dayjs().utcOffset();
  return dayjs.unix(timestamp).subtract(offset, 'm').format('MMMM Do, Y');
};

/**
 * @description gets the fully spelled out date with time (ex. January 19, 2021 12:22:01 AM)
 * @param timestamp
 * @returns
 */
export const fullDateWithTime = (timestamp: number): string => {
  return dayjs.utc(timestamp).format('MMMM D, YYYY h:mm:ssa');
};

/**
 * @description gets the full date (ex. January 19, 2021)
 * @param timestamp
 * @returns
 */
export const fullDate = (timestamp: number): string => {
  return dayjs.utc(timestamp).format('MMMM D, YYYY');
};

/**
 * @description creates a date range to use as a default
 * @param rangeType
 * @returns
 */
export const getPredefinedDateRange = (
  rangeType: 'week' | 'month' | 'year'
): IRange => {
  const toMoment = dayjs().endOf('day');
  let fromMoment: dayjs.Dayjs;
  switch (rangeType) {
    case 'week':
      fromMoment = dayjs().subtract(1, 'week').startOf('day');
      return {
        from: {
          label: fromMoment.format('M/D/YY'),
          value: fromMoment.valueOf(),
        },
        to: {
          label: toMoment.format('M/D/YY'),
          value: toMoment.valueOf(),
        },
      };
    case 'month':
      fromMoment = dayjs().subtract(1, 'month').startOf('day');
      return {
        from: {
          label: fromMoment.format('M/D/YY'),
          value: fromMoment.valueOf(),
        },
        to: {
          label: toMoment.format('M/D/YY'),
          value: toMoment.valueOf(),
        },
      };
    case 'year':
      fromMoment = dayjs().subtract(1, 'year').startOf('day');
      return {
        from: {
          label: fromMoment.format('M/D/YY'),
          value: fromMoment.valueOf(),
        },
        to: {
          label: toMoment.format('M/D/YY'),
          value: toMoment.valueOf(),
        },
      };
    default:
      fromMoment = dayjs(0);
      return {
        from: {
          label: fromMoment.format('M/D/YY'),
          value: fromMoment.valueOf(),
        },
        to: {
          label: toMoment.format('M/D/YY'),
          value: toMoment.valueOf(),
        },
      };
  }
};
